import { useCallback, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { canAccessAdmin } from "../utils/permissions";
import {
  ShieldCheck, Clock, CheckCircle, Warning, SpinnerGap,
  ArrowClockwise, Plus, ChartBar, Users, User
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

const CATEGORIES = [
  { id: "conformity", label: "Conformité", color: "blue" },
  { id: "products", label: "Produits financiers", color: "violet" },
  { id: "ethics", label: "Ethique & Deontologie", color: "amber" },
  { id: "technical", label: "Technique", color: "emerald" },
  { id: "general", label: "General", color: "zinc" },
];

const TYPES = [
  { id: "video", label: "Video visionnee" },
  { id: "quiz", label: "Quiz complete" },
  { id: "webinar", label: "Webinaire" },
  { id: "course", label: "Cours / Formation" },
  { id: "reading", label: "Lecture" },
];

export default function AMFCompliancePage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [myHours, setMyHours] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: "video", title: "", hours: "", category: "general", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("my");

  // Eric 2026-06-25 (iter118 B3) — migré vers canAccessAdmin (helper canonique,
  // aligné backend require_admin). Inclut désormais founder/admin/ceo/president/vp.
  const isAdmin = canAccessAdmin(user);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [hoursRes, teamRes] = await Promise.all([
        axios.get(`${API}/api/amf/my-hours`, { withCredentials: true }),
        isAdmin ? axios.get(`${API}/api/amf/team-compliance`, { withCredentials: true }).catch(() => ({ data: null })) : Promise.resolve({ data: null })
      ]);
      setMyHours(hoursRes.data);
      if (teamRes.data) setTeamData(teamRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const logActivity = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/amf/log-activity`, { ...form, hours: parseFloat(form.hours) }, { withCredentials: true });
      setShowForm(false);
      setForm({ type: "video", title: "", hours: "", category: "general", description: "" });
      loadData();
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={32} className="animate-spin text-primary" /></div>;

  const pct = myHours?.completion_pct || 0;
  const circleColor = pct >= 100 ? "#22c55e" : pct >= 50 ? "#eab308" : "#ef4444";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="amf-compliance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/20 flex items-center justify-center">
            <ShieldCheck size={24} className="text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit']" data-testid="amf-title">{t("amf_title")}</h1>
            <p className="text-xs text-muted-foreground">{t("amf_subtitle")}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData} className="gap-1.5"><ArrowClockwise size={14} /> Rafraichir</Button>
          <Button size="sm" onClick={() => setShowForm(true)} className="gap-1.5" data-testid="log-hours-btn"><Plus size={14} /> Enregistrer des heures</Button>
        </div>
      </div>

      {/* Tabs */}
      {isAdmin && (
        <div className="flex gap-1 bg-secondary/50 rounded-lg p-1 w-fit">
          <button onClick={() => setTab("my")} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === "my" ? "bg-card shadow-sm" : "text-muted-foreground"}`} data-testid="tab-my">
            <User size={14} className="inline mr-1.5" />Mes heures
          </button>
          <button onClick={() => setTab("team")} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === "team" ? "bg-card shadow-sm" : "text-muted-foreground"}`} data-testid="tab-team">
            <Users size={14} className="inline mr-1.5" />Equipe
          </button>
        </div>
      )}

      {tab === "my" && myHours && (
        <>
          {/* Progress Circle */}
          <div className="bg-card border border-border rounded-xl p-6 flex items-center gap-8" data-testid="amf-progress">
            <div className="relative w-28 h-28 flex-shrink-0">
              <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="text-secondary" />
                <circle cx="50" cy="50" r="42" fill="none" stroke={circleColor} strokeWidth="6" strokeLinecap="round" strokeDasharray={`${(pct / 100) * 263.9} 263.9`} className="transition-all duration-1000" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-2xl font-black font-['Outfit']">{pct}%</span>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-lg font-['Outfit']">{myHours.total_hours}h / {myHours.required_hours}h</h2>
              <p className="text-sm text-muted-foreground">{t("continuing_education")} {new Date().getFullYear()}</p>
              <div className="flex items-center gap-2 mt-2">
                {pct >= 100 ? (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-500/10 text-teal-400 rounded-full text-xs font-medium border border-teal-500/20"><CheckCircle size={12} weight="fill" /> Conforme</span>
                ) : (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 text-amber-400 rounded-full text-xs font-medium border border-amber-500/20"><Clock size={12} /> En cours — {myHours.required_hours - myHours.total_hours}h restantes</span>
                )}
              </div>
            </div>
          </div>

          {/* Activities */}
          {myHours.activities?.length > 0 && (
            <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="amf-activities">
              <div className="px-5 py-3 border-b border-border"><h3 className="font-semibold text-sm">{t("activities_recorded")} ({myHours.activities.length})</h3></div>
              <div className="divide-y divide-border">
                {myHours.activities.map(a => (
                  <div key={a.id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{a.title}</p>
                      <p className="text-xs text-muted-foreground">{TYPES.find(t => t.id === a.type)?.label || a.type} — {CATEGORIES.find(c => c.id === a.category)?.label || a.category}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-sm">{a.hours}h</span>
                      {a.verified && <CheckCircle size={14} className="text-teal-400" weight="fill" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tab === "team" && teamData && (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="team-compliance">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold text-sm">{t("team_compliance")}</h3>
            <span className="text-xs text-muted-foreground">{teamData.compliant}/{teamData.total} conformes ({teamData.compliance_rate}%)</span>
          </div>
          <div className="divide-y divide-border">
            {teamData.members.map(m => (
              <div key={m.id} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${m.status === "compliant" ? "bg-teal-500" : m.status === "in_progress" ? "bg-amber-500" : "bg-rose-500"}`} />
                  <div>
                    <p className="text-sm font-medium">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${m.completion_pct >= 100 ? "bg-teal-500" : m.completion_pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${m.completion_pct}%` }} />
                  </div>
                  <span className="text-xs font-medium w-14 text-right">{m.hours_completed}/{m.hours_required}h</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Log Activity Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="log-activity-form">
            <h3 className="font-bold text-lg mb-4">{t("log_activity")}</h3>
            <form onSubmit={logActivity} className="space-y-3">
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={t("activity_title")} required className="w-full h-10 px-3 bg-background border border-border rounded-lg text-sm" data-testid="activity-title" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="h-10 px-3 bg-background border border-border rounded-lg text-sm" data-testid="activity-type">
                  {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                <input type="number" step="0.5" min="0.5" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} placeholder="Heures" required className="h-10 px-3 bg-background border border-border rounded-lg text-sm" data-testid="activity-hours" />
              </div>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-lg text-sm" data-testid="activity-category">
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">Annuler</Button>
                <Button type="submit" disabled={submitting} className="flex-1 gap-1.5" data-testid="submit-activity-btn">
                  {submitting ? <SpinnerGap size={14} className="animate-spin" /> : <Plus size={14} />} Enregistrer
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
